import React from 'react';
import { RequestType, RequestTypeIds } from '@src/utils/enums';
import { nprQuestionsById as nprQuestionsByIdV1 } from '@pages/Forms/input-forms/NprForm/config/questions/nprData.questions';
import { sectionsById as sectionsByIdV1 } from '@pages/Forms/input-forms/NprForm/config/questions/nprData.sections';
import { nprQuestionsByIdV2 } from '@pages/Forms/input-forms/NprForm/config/questions/nprData.questions.V2';
import { sectionsByIdV2 } from '@pages/Forms/input-forms/NprForm/config/questions/nprData.sections.V2';
import { getNprInstructionsV1 } from '@pages/Forms/static-forms/Npr/config/nprData.instructions.V1';
import { getCertifySignSubmitV1 } from '@pages/Forms/input-forms/NprForm/config/certify/nprData.certify.V1';
import {
  getParentObject,
  nprQuestionNameById,
  nprQuestionIdByName,
} from '@pages/Forms/input-forms/NprForm/nprData.types';
import {
  nprQuestionNameByIdV2,
  getParentObjectV2,
  nprQuestionIdByNameV2,
} from '@pages/Forms/input-forms/NprForm/nprData.types.V2';
import { getLatestFormVersionByRequestType } from '@pages/Forms/registry/latestVersions';
import { QuestionDataById } from '@components/dynamic-form/types';

export type NprVariant = 'npr' | 'abNpr';

export type NprInstructionsConfig = {
  headerText: string;
  metadataHeader: string;
  paragraphs: React.ReactNode[];
};

export type NprCertifyConfig = {
  resultBanner?: (args: { resultMessage: string; actionMessage: string }) => React.ReactNode;
  notAuthorizedBanner?: (args: { requestOwner: { firstName: string; lastName: string } }) => React.ReactNode;
  headerText: string;
  headerSubText: React.ReactNode;
  agreeHeading: string;
  agreeBullets: (args: { showAgentBullet: boolean }) => React.ReactNode;
};

type SectionsById = typeof sectionsByIdV1 | typeof sectionsByIdV2;

export type NprConfig = {
  questionsById: QuestionDataById;
  sectionsById: SectionsById;
  nprQuestionNameById: typeof nprQuestionNameById | typeof nprQuestionNameByIdV2;
  nprQuestionIdByName: typeof nprQuestionIdByName | typeof nprQuestionIdByNameV2;
  getParentObject: typeof getParentObject | typeof getParentObjectV2;
  getInstructions: (args: { variant: NprVariant; onJdLink: () => void }) => NprInstructionsConfig;
  getCertifySignSubmit: (args: { variant: NprVariant }) => NprCertifyConfig;
};

/**
IMPORTANT: Key format is `${variant}@${version}`.
The `variant` string must match what resolveNprVariant() returns.
If resolveNprVariant returns `abNprV3`, this file must contain keys like `abNprV3@3`.
*/
type NprConfigKey = `${NprVariant}@${number}`;

export const nprConfigByKey: Record<NprConfigKey, NprConfig> = {
  'npr@1': {
    questionsById: nprQuestionsByIdV1,
    sectionsById: sectionsByIdV1,
    nprQuestionNameById,
    nprQuestionIdByName,
    getParentObject,
    getInstructions: ({ variant, onJdLink }) => getNprInstructionsV1({ variant, onJdLink }),
    getCertifySignSubmit: () => getCertifySignSubmitV1(),
  }, // legacy mapping

  'abNpr@1': {
    questionsById: nprQuestionsByIdV1,
    sectionsById: sectionsByIdV1,
    nprQuestionNameById,
    nprQuestionIdByName,
    getParentObject,
    getInstructions: ({ variant, onJdLink }) => getNprInstructionsV1({ variant, onJdLink }),
    getCertifySignSubmit: () => getCertifySignSubmitV1(),
  },
  'abNpr@2': {
    questionsById: nprQuestionsByIdV2,
    sectionsById: sectionsByIdV2,
    nprQuestionNameById: nprQuestionNameByIdV2,
    nprQuestionIdByName: nprQuestionIdByNameV2,
    getParentObject: getParentObjectV2,
    getInstructions: ({ variant, onJdLink }) => getNprInstructionsV1({ variant, onJdLink }),
    getCertifySignSubmit: () => getCertifySignSubmitV1(),
  },
};

export const resolveNprConfig = (variant: NprVariant, version: number): NprConfig => {
  const configsForVariant = (Object.entries(nprConfigByKey) as Array<[NprConfigKey, NprConfig]>)
    .filter(([key]) => key.startsWith(`${variant}@`))
    .map(([key, config]) => ({
      version: Number(key.split('@')[1]),
      config,
    }))
    .sort((a, b) => a.version - b.version);

  if (configsForVariant.length === 0) {
    console.error(`No NPR configs found for variant="${variant}". Check nprVariantConfig + nprConfig keys.`);
  }

  return (
    configsForVariant.find((c) => c.version === version)?.config ??
    [...configsForVariant].reverse().find((c) => c.version <= version)?.config ??
    configsForVariant[configsForVariant.length - 1].config
  );
};

export const resolveNprConfigForRequest = (args: {
  requestType: string | undefined;
  formVersion: number | null | undefined;
}): NprConfig => {
  const { requestType } = args;
  const isAb = requestType === RequestType.ActivityBasedNoPermitRequired;
  const variant: NprVariant = isAb ? 'abNpr' : 'npr';
  const requestTypeId = isAb ? RequestTypeIds.ActivityBasedNoPermitRequired : RequestTypeIds.NoPermitRequired;
  const versionToUse = getLatestFormVersionByRequestType(requestTypeId);

  return resolveNprConfig(variant, versionToUse!);
};
